<script>
import courses from '@/global/js/courses'
export default {
  name: 'GuardianDashboard',
  watch: {
    user (isUser) {
      this.retrieveEntities()
    }
  },
  data () {
    return {
      courses,
      classes: [],
      students: [],
      nextClassDates: {},
      application: null,
      appCount: 0,
      invoices: [],
    }
  },
  computed: {
    isAdultStudent () {
      return this.user.user_type.includes('ADULT_STUDENT')
    },
    isGuardian () {
      return this.user.user_type.includes('GUARDIAN')
    },
    user () {
      return this.$store.state.user
    },
    greetingPhrase () {
      return `Good ${this.$getTimeOfDayName()}, ${this.user.name.split(' ')[0]}.`
    },
    paperwordNeedsCompleting () {
      return this.students.find(student => student.paperwork_complete === false)
    },
  },
  methods: {
    getStudents () {
      console.log('oh hi')
      if (!this.user) return
      return new Promise(async (resolve, reject) => {
        try {
          this.students = (await this.$firestore.collection('users').where('guardian_id', '==', this.user.id).get())
            .docs.map(doc => doc.data())
          this.students.forEach(student => this.nextClassDates[student.id] = '...')
          if(this.isAdultStudent) this.students.push(this.user)
        } catch (e) {
          this.$toast({
            copy: 'Could not retrieve students.',
            class: 'alert',
          })
          console.error(e)
        } finally {
          resolve()
        }
      })
    },
    async getApplication () {
      if (!this.user) return
      if (['APPROVED','FULLY_PAID',].includes(this.user.application_status)) return //only if application makes sense to retrieve
      try {
        let applications = (await this.$firestore.collection('scholarship_applications').where('guardian_id', '==', this.user.id).get()).docs.map(doc => doc.data())
        // make sure we grab the most recent scholarship
        applications.sort((a, b) => b.created - a.created)
        this.appCount = applications.length
        this.application = applications[0]
      } catch (e) {console.log('No application to retrieve.')}
    },
    async getNextClassDates () {
      const nextClassPromises = this.students.map(student => this.$HTTP({
        method: 'get',
        uri: `users/${student.id}/next-class-date`,
      }))
      const responses = await Promise.all(nextClassPromises)
      responses.forEach((res, idx) => {
        this.nextClassDates[this.students[idx].id] = 
          this.$buildDate(res.body.date, `{longDay}, {longMonth} {date} at ${this.$format24to12(res.body.start_time)}`)
      })
      this.$forceUpdate()
    },
    async getInvoices() {
      if (!this.user) return
      return new Promise(async (resolve, reject) => {
        try {
          this.invoices = (await this.$firestore.collection('invoices')
            .where('guardian_id', '==', this.user.id)
            .where('status', '==', 'SENT').get())
              .docs.map(doc => doc.data())
        } catch (e) {
          this.$toast({
            copy: 'Could not retrieve invoices.',
            class: 'alert',
          })
          console.error(e)
        } finally {
          resolve()
        }
      })
    },
    async retrieveEntities () {
      if (!this.user) {
        this.$router.replace({name: "Home", params: {login: true}})
        return
      }
      await this.getStudents()
      await this.getApplication()
      await this.getInvoices()
      //this.getNextClassDates()
    },
    openPaperwork () {
      this.$modals.show({
        name: 'paperwork',
        guardianId: this.user.id,
        hardClose: true,
        onSuccess: this.retrieveEntities,
      })
    },
    openScholarshipOffer () {
      this.$modals.show({
        name: 'scholarship-offer',
        guardian: this.user,
        application: this.application,
        onSuccess: async () => {
          const user = (await this.$firestore.doc(`users/${this.user.id}`).get()).data()
          this.$store.commit('update', {user})
        },
      })
    },
    openInvoice (invoice) {
      this.$modals.show({
        name: 'pay-invoice',
        guardian: this.user,
        invoice: invoice,
        onSuccess: this.getInvoices
      })
    },
    openStudent (student) {
      console.log(student)
      this.$modals.show({
        name: 'create-student',
        onSuccess: this.getStudents,
        info: student,
        hardClose: false,
      })
    },
  },
  async mounted () {
    this.$forceUpdate()
    this.classes = await this.$classes().classList()
    this.retrieveEntities()
  },
}
</script>

<template lang="pug">
  .guardian-dashboard-main
    .guardian-dashboard-container(
      v-if='user'
    )
      .header
        .title SBCS User Portal
        .description This portal allows you to check the status of your payment, scholarship request and confirm what classes you{{isAdultStudent && !isGuardian ? ' have' : ' and you'}}{{isGuardian ? 'r participant has' : ''}} signed up for. If you have any additional questions about South Bend Code School, please click the 
          span.blue-help-button 
            i.material-icons help_outline
            | Help
          | button in the lower right of the screen.
      .greeting {{greetingPhrase}}
      .warning(
        v-if='paperwordNeedsCompleting'
        @click='openPaperwork'
      )
        .copy
          .title Action Required
          .explain Please complete our Participant Paperwork before your participant’s first coding session. Click here to get started.
        .action
          i.material-icons arrow_forward
      .warning(
        v-if='["OFFER_SENT", "APPROVED_NOT_PAID",].includes(user.application_status)'
        @click='openScholarshipOffer'
      )
        .copy
          .title Scholarship Update
          .explain A scholarship has been offered. Click to see details.
        .action
          i.material-icons arrow_forward
      .invoices.students-summary(
        v-if='invoices.length'
      )
        .invoice-title.student-summary-title.font-2 Invoices
        .invoice-holder
          .invoice(
            v-for='invoice in invoices'
          ) 
            .amount Amount: 
              b ${{invoice.amount}}
            .date Date: {{new Date(invoice.created).toLocaleDateString()}}
            .message {{invoice.message}}
            .pay-button.pro-button.minor(
              @click='openInvoice(invoice)'
            ) Pay
      .students-summary(
        v-if='["PENDING", "SCHOLARSHIP_REBUTTED"].includes(user.application_status)'
      )
        .student-summary-title.font-2 Scholarship Status:
        .application-status.PENDING Pending 
          span.app-count(v-if='appCount > 1') ({{appCount}})
        .status-explain
          | We are looking at your scholarship application{{appCount > 1 ? 's' : ''}}. You will
          | hear from us within 48 hours of applying.
      .students-summary(
        v-else-if='user.application_status === "DENIED"'
      )
        .student-summary-title.font-2 Scholarship Status:
        .application-status.DENIED Denied
        .status-explain
          | Your application for scholarship has been denied.
      .students-summary(
        v-if='students.length'
      )
        .student-summary-title.font-2 Participant Summary
        .student-summary-description The Participant Summary allows you to see what classes you and your enrollees are signed up for, the description of the class, and the day/time the class takes place. If you need to change the class dates please click the 
          span.blue-help-button 
            i.material-icons help_outline
            | Help
          | button in the lower right of the screen.
        .add-participant
          .copy Want to sign up another participant? {{!user.user_type.includes('ADULT_STUDENT') ? ' Interested in enrolling in our adult classes yourself?' : ''}} Select one of the options below to get started!
          .actions
            .button-holder
              router-link(
                :to='{name: "GuardianSignUp", params: {newStudent: true, isAdultStudent: false}}'
              ) 
                .button.pro-button.minor Kids and Teens (7 - 18)
            .button-holder 
              router-link(
                :to='{name: "GuardianSignUp", params: {newStudent: true, isAdultStudent: true}}'
              ) 
                .button.pro-button.minor Adult Learner (18+)
        .student-summaries-holder
          .student-summary(
            v-for='student in students'
          )
            .name.font-2.dotdotdot {{student.name}}
            .course(
              v-for='course in $student(student, classes).allCourses'
            ) 
              .course-name {{course.name}}
              .details-holder
                .detail.label Class day:
                .detail {{$student(student, classes).enrolledDates[course.id].day.join(', ')}}
                .detail.label Class time: 
                .detail {{$student(student, classes).enrolledDates[course.id].time.join(', ')}}
                .detail.label Class dates: 
                .detail {{$student(student, classes).enrolledDates[course.id].details.join(', ')}}
            .sign-up {{!$student(student, classes).allCourses.length ? `${student.name} isn\'t enrolled in any current or upcoming classes. To sign them` : `To sign ${student.name}`}} up for more classes, click 
              router-link(
                :to='{name: "GuardianSignUp", params: {studentId: student.id, isAdultStudent: student.user_type.includes("ADULT_STUDENT")}}'
              ) here
              |.
            .actions
              .action-link(
                @click='openStudent(student)'
              ) Edit student info
        .actions(
          v-if='isGuardian'
        )
          .action-link(
            v-if='!paperwordNeedsCompleting'
            @click='openPaperwork'
          ) Edit emergency contact and media release info
          a.curriculum-link(
            href='https://south-bend-code-works.github.io/sbcs-course-guide/'
            target='_blank'
          ) Click here to view our course guides.
            i.material-icons chevron_right
      a.faq-area(
        href='https://drive.google.com/file/d/1FWSWxhx3FjHtpSGwJISt2IU5t26gOpj9/view'
        target='_blank'
      ) Click here to view frequently asked questions.
        i.material-icons chevron_right
        
</template>

<style lang="sass" scoped>
  .blue-help-button
    $buttonSize: 10px
    background-color: #56BDF9
    padding: .25rem .5rem
    border-radius: 25rem
    margin-right: 0.35rem
    display: inline-grid
    grid-template-columns: $buttonSize auto
    grid-column-gap: 2px
    align-items: center
    font-weight: bold
    position: relative
    top: 1px
    font-size: $buttonSize
    > i
      font-size: $buttonSize
      height: $buttonSize

  .guardian-dashboard-main
    .guardian-dashboard-container
      padding: 3rem
      max-width: 900px
      @media(max-width:700px)
        padding: 1rem
      > .header
        margin-bottom: 2rem
        width: fit-content
        .title
          font-size: 48px
          font-weight: bold
        .description
          //line-height: 1.5
          // max-width: 900px
          font-size: 14px
      .warning
        background-color: transparentize($red, .4)
        padding: 16px
        box-shadow: 0 0 8px 0 #888
        border-radius: 16px
        margin-bottom: 32px
        width: fit-content
        cursor: pointer
        transition: .25s all
        display: grid
        grid-template-columns: auto 100px
        &:hover
          box-shadow: 0 0 8px 4px #888
        .copy
          border-right: black solid thin
          padding-right: 16px
          .title
            font-size: 36px
            font-family: $font-2
            text-decoration: underline
            margin-bottom: 8px
          .explain
            max-width: 500px
            font-size: 14px
        .action
          display: grid
          align-items: center
          justify-items: center
          > i
            font-size: 48px
      .greeting
        font-size: 22px
        margin-bottom: 2rem
      .students-summary
        margin: 2rem 0
        .add-participant
          border-radius: 0.5rem
          background-color: $lightgrey
          width: calc(100% - 2rem)
          margin: 2rem auto
          padding: 1rem
          .copy
            font-weight: bold
            font-size: 1.25rem
            text-align: center
          .actions
            margin: 1rem auto 0
            width: fit-content
            .button-holder
              display: inline-block
              margin: 1rem
              a
                text-decoration: none
              @media(max-width:700px)
                margin: auto
                margin-bottom: 1rem
                display: block
                width: fit-content
        .actions
          .action-link
            text-decoration: underline
            color: blue
            width: fit-content
            cursor: pointer
            margin-top: 0.5rem
        .student-summary-title
          font-size: 24px
          color: 24px
          margin-bottom: .5rem
          max-width: 500px
          display: inline-block
        .student-summary-description
          font-size: 14px
          border-bottom: thin solid $darkgrey
          padding-bottom: 1rem
          margin-bottom: 1rem
          // max-width: 800px
        .application-status
          display: inline-block
          font-size: 24px
          width: fit-content
          font-weight: bold
          margin-left: 1rem
        .status-explain
          font-size: 16px
          color: $darkgrey
          //max-width: 400px
          margin-top: 1rem
        .student-summaries-holder
          display: flex
          flex-flow: wrap
          .student-summary
            width: 40%
            padding: 25px
            // border: thin solid $mediumgrey
            margin: 0 8px 16px
            cursor: default
            box-shadow: 0 0 3px 0 black
            border-radius: 10px
            height: fit-content
            @media (max-width: 600px)
              width: calc(100vw - 4rem)
              padding: 1rem
              margin-left: 0
            .name
              font-size: 36px
              width: 352px
            .course
              margin-bottom: 3rem
              .credits-remaining
                font-size: 14px
                color: $darkgrey
              .details-holder
                padding-top: 1rem
                margin-top: 1rem
                border-top: thin $mediumgrey solid
                display: grid
                grid-template-columns: 7rem 1fr
                .detail
                  font-size: 14px
        .sign-up
          color: $orange
      .invoices
        .invoice-holder
          display: flex
          flex-flow: wrap
          .invoice
            min-width: 20rem
            max-width: 100%
            padding: 1.5rem
            margin: 0.5rem
            border-radius: 0.25rem
            border: thin $mediumgrey solid
            .message
              margin-top: 0.5rem
            .pay-button
              margin: 1rem auto 0 auto
      .faq-area, .curriculum-link
        text-decoration: none
        color: black
        align-items: center
        display: grid
        width: fit-content
        grid-template-columns: auto min-content
        color: white
        padding: 1rem
        border-radius: 1rem
        margin: 3rem 0
        font-weight: bold
        background-color: $aqua
</style>